import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Table, Button, Icon } from 'semantic-ui-react';
import i18next from 'i18next';

import DayCellHeader from './DayCellHeader/DayCellHeader';
import DayCellDropdown from './DayCellDropdown/DayCellDropdown';
import DayCellForm from './DayCellForm/DayCellForm';
import DayCellOptions from './DayCellOptions/DayCellOptions';
import SchedulEditPanel from './SchedulEditPanel/SchedulEditPanel';
import GoogleOpeningHoursFetcher from './GoogleOpeningHoursFetcher/GoogleOpeningHoursFetcher';

import './WeekScheduler.scss';

// TODO: Block the addition of new segments if one segment is open/closed all day (it makes no sense)
// TODO: Let the user know if two segments are congruent
// TODO: Make sure the logic of calculating if service is available takes into account multiple segments as well
class WeekScheduler extends Component {
    defaultState = { weeklySegments: {} };
    state = { ...this.defaultState };

    constructor(props) {
        super(props);

        this.calculateTableStructure = this.calculateTableStructure.bind(this);
        this.generateCellElementFromSegment = this.generateCellElementFromSegment.bind(this);
        this.updateDaySegment = this.updateDaySegment.bind(this);
        this.resetSchedule = this.resetSchedule.bind(this);
        this.saveScheduleEdit = this.saveScheduleEdit.bind(this);
        this.removeSegmentFromDay = this.removeSegmentFromDay.bind(this);
        this.addSegmentToDay = this.addSegmentToDay.bind(this);
        this.eligbleForScheduleEditSave = this.eligbleForScheduleEditSave.bind(this);
        this.eligbleForScheduleEditCancel = this.eligbleForScheduleEditCancel.bind(this);
    }

    componentWillMount() {
        this.resetSchedule();
    }

    componentDidUpdate(prevProps) {
        if (this.props.restaurant.id !== prevProps.restaurant.id) {
            this.resetSchedule();
        }
    }

    updateDaySegment(day, segmentIndex, segmentUpdate) {
        const updatedSchedule = { ...this.state.weeklySegments };
        if (segmentUpdate.closeTime) {
            segmentUpdate = this.handleCloseTimeChange(updatedSchedule[day][segmentIndex], segmentUpdate);
        }
        updatedSchedule[day][segmentIndex] = { ...updatedSchedule[day][segmentIndex], ...segmentUpdate };

        this.setState({ weeklySegments: updatedSchedule });
    }

    handleCloseTimeChange(originalSegment, segmentUpdate) {
        if (originalSegment.openTime >= segmentUpdate.closeTime) {
            const nextDay = originalSegment.days.from === 7 ? 1 : originalSegment.days.from + 1;
            segmentUpdate.days = { from: originalSegment.days.from, until: nextDay };
        } else {
            segmentUpdate.days = { from: originalSegment.days.from, until: originalSegment.days.from };
        }

        return segmentUpdate;
    }

    resetSchedule(schedule = this.props.schedule) {
        const segmentedSchedule = [];
        schedule.forEach(segment => {
            segmentedSchedule[segment.days.from] = segmentedSchedule[segment.days.from] || [];
            segmentedSchedule[segment.days.from].push(segment);
        });

        this.setState({ weeklySegments: segmentedSchedule });
    }

    saveScheduleEdit() {
        let segments = [];

        for (let day in this.state.weeklySegments) {
            segments = segments.concat(this.state.weeklySegments[day]);
        }
        this.props.updatePeriods(segments, () => this.resetSchedule());
    }

    removeSegmentFromDay(day, index) {
        const updatedSchedule = { ...this.state.weeklySegments };

        if (this.state.weeklySegments[day].length > 1) {
            updatedSchedule[day].splice(index, 1);
        } else {
            updatedSchedule[day][index] = this.generateNewSegment(day);
        }

        this.setState({ weeklySegments: updatedSchedule });
    }

    addSegmentToDay(day) {
        const updatedSchedule = { ...this.state.weeklySegments };
        const newSegment = this.generateNewSegment(day);
        updatedSchedule[day].push(newSegment);

        this.setState({ weeklySegments: updatedSchedule });
    }

    figureSegmentState(segment) {
        if (segment.allDay) return 'allDay';
        if (segment.closed) return 'closed';
        return 'exactHours';
    }

    eligbleForScheduleEditSave() {
        const dailySegments = Object.values(this.state.weeklySegments).reduce((day, nextDay) => day.concat(nextDay));
        return dailySegments.some((segment, index) => {
            return (segment.openTime !== '' && segment.closeTime !== '') && !this.segmentComparison(segment, this.props.schedule[index]);
        });
    }

    eligbleForScheduleEditCancel() {
        const dailySegments = Object.values(this.state.weeklySegments).reduce((day, nextDay) => day.concat(nextDay));
        return dailySegments.some((segment, index) => {
            return !this.segmentComparison(segment, this.props.schedule[index]);
        });
    }

    segmentComparison(segment1, segment2) {
        return segment1.days.from === segment2.days.from &&
            segment1.days.until === segment2.days.until &&
            segment1.allDay === segment2.allDay &&
            segment1.closed === segment2.closed &&
            segment1.openTime.substring(0, 5) === segment2.openTime.substring(0, 5) &&
            segment1.closeTime.substring(0, 5) === segment2.closeTime.substring(0, 5);
    }

    generateNewSegment(day) {
        day = Number(day);

        return {
            days: { from: day, until: day === 7 ? 0 : day + 1 },
            allDay: false,
            closed: true,
            openTime: '00:00:00',
            closeTime: '00:00:00'
        };
    }

    generateCellElementFromSegment(CellElement, segment, index, rest) {
        return (
            <CellElement key={segment.days.from + '' + index}
                segment={segment}
                
                {...rest}
            />
        );
    }

    calculateTableStructure() {
        const tableStrucutre = { totalCols: 0, headerCols: [], segmentOverviewCols: [], segmentEditCols: [], segmentOptionsCols: [] };

        for (let day in this.state.weeklySegments) {

            this.state.weeklySegments[day].forEach((segment, index) => {
                tableStrucutre.totalCols++;

                tableStrucutre.segmentOverviewCols.push(this.generateCellElementFromSegment(DayCellDropdown, segment, index, {
                    figureSegmentState: this.figureSegmentState,
                    updateSegment: (updatedSegment) => this.updateDaySegment(day, index, updatedSegment)
                }));
                tableStrucutre.segmentEditCols.push(this.generateCellElementFromSegment(DayCellForm, segment, index, {
                    figureSegmentState: this.figureSegmentState,
                    updateSegment: (updatedSegment) => this.updateDaySegment(day, index, updatedSegment)
                }));
                tableStrucutre.segmentOptionsCols.push(this.generateCellElementFromSegment(DayCellOptions, segment, index, {
                    removeSegment: () => this.removeSegmentFromDay(day, index),
                }));

            });

            tableStrucutre.headerCols.push(
                <DayCellHeader key={day}
                    day={Number(day)}
                    numberOfSegments={this.state.weeklySegments[day].length}
                    addSegmentToDay={this.addSegmentToDay}
                />
            );
        }


        return tableStrucutre;
    }

    render() {
        const { restaurant, style } = this.props;

        const tableStrucutre = this.calculateTableStructure();

        return (
            <Table celled structured unstackable columns={tableStrucutre.totalCols} style={style}>
                <Table.Header>
                    <Table.Row textAlign='center' >
                        {tableStrucutre.headerCols}
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    <Table.Row>
                        {tableStrucutre.segmentOverviewCols}
                    </Table.Row>
                    <Table.Row>
                        {tableStrucutre.segmentEditCols}
                    </Table.Row>
                    <Table.Row>
                        {tableStrucutre.segmentOptionsCols}
                    </Table.Row>
                </Table.Body>

                <Table.Footer>
                    <Table.Row>
                        <Table.HeaderCell colSpan={tableStrucutre.totalCols}>
                            <SchedulEditPanel
                                saveScheduleEdit={this.saveScheduleEdit}
                                cancelScheduleEdit={this.resetSchedule}
                                eligbleForScheduleEditSave={this.eligbleForScheduleEditSave}
                                eligbleForScheduleEditCancel={this.eligbleForScheduleEditCancel}
                                />
                            <GoogleOpeningHoursFetcher
                                restId={restaurant.id}
                                placeId={restaurant.placeId}
                                updateSchedule={(newSchedule) => {
                                    this.resetSchedule(newSchedule);
                                }}
                                />
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Footer>
            </Table>
        );
    }
}

WeekScheduler.propTypes = {
    restaurant: PropTypes.object,
    schedule: PropTypes.array,
    updatePeriods: PropTypes.func,
    style: PropTypes.object,
    
};

export default WeekScheduler;